// src/pages/AboutPage/AboutPage.js
import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faGraduationCap,
    faHandHoldingHeart,
    faLightbulb,
    faUsers,
    faChartLine,
    faSmile,
    faStar
} from '@fortawesome/free-solid-svg-icons';
import Navbar from '../../components/common/Navbar/Navbar';
import Footer from '../../components/common/Footer/Footer';
import ScrollToTop from '../../components/common/ScrollToTop/ScrollToTop';
import useScrollReveal from '../../hooks/useScrollReveal';
import './AboutPage.css';

const AboutPage = () => {
    // Use the custom hook for scroll reveal animation
    useScrollReveal();

    return (
        <div className="about-page">
            <Navbar />

            <main>
                {/* Hero Section */}
                <section className="about-hero">
                    <div className="container">
                        <div className="hero-content reveal">
                            <h1>About Abhyaas</h1>
                            <p className="lead">Nurturing young minds with quality education and creative learning experiences</p>
                        </div>
                    </div>
                    <div className="hero-wave"></div>
                </section>

                {/* Mission & Vision Section */}
                <section className="mission-vision-section">
                    <div className="container">
                        <div className="mission-vision-grid">
                            <div className="mission-card reveal">
                                <div className="card-icon">
                                    <FontAwesomeIcon icon={faHandHoldingHeart} />
                                </div>
                                <div className="card-content">
                                    <h2>Our Mission</h2>
                                    <p>
                                        To provide a nurturing educational environment that encourages curiosity,
                                        creativity, and critical thinking while respecting the unique potential of
                                        each child. We aim to build a strong foundation for lifelong learning through
                                        a balanced approach to education.
                                    </p>
                                </div>
                            </div>

                            <div className="vision-card reveal">
                                <div className="card-icon">
                                    <FontAwesomeIcon icon={faLightbulb} />
                                </div>
                                <div className="card-content">
                                    <h2>Our Vision</h2>
                                    <p>
                                        To be recognized as a leader in innovative education that prepares children
                                        to become confident, compassionate, and responsible global citizens. We envision
                                        our students as future changemakers who positively impact their communities.
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                {/* Core Values Section */}
                <section className="values-section">
                    <div className="container">
                        <div className="section-header reveal">
                            <span className="section-subtitle">Our Philosophy</span>
                            <h2 className="section-title">Core Values</h2>
                            <div className="section-description">
                                <p>These principles guide our approach to education and shape our learning environment</p>
                            </div>
                        </div>

                        <div className="values-grid reveal">
                            <div className="value-card">
                                <div className="value-icon">
                                    <FontAwesomeIcon icon={faGraduationCap} />
                                </div>
                                <div className="value-content">
                                    <h3>Academic Excellence</h3>
                                    <p>We maintain high standards in our curriculum and teaching methods to foster intellectual growth and academic achievement.</p>
                                </div>
                            </div>

                            <div className="value-card">
                                <div className="value-icon">
                                    <FontAwesomeIcon icon={faUsers} />
                                </div>
                                <div className="value-content">
                                    <h3>Community</h3>
                                    <p>We believe in building strong partnerships between students, parents, teachers, and the wider community.</p>
                                </div>
                            </div>

                            <div className="value-card">
                                <div className="value-icon">
                                    <FontAwesomeIcon icon={faChartLine} />
                                </div>
                                <div className="value-content">
                                    <h3>Continuous Improvement</h3>
                                    <p>We are committed to ongoing assessment and enhancement of our programs, facilities, and teaching methods.</p>
                                </div>
                            </div>

                            <div className="value-card">
                                <div className="value-icon">
                                    <FontAwesomeIcon icon={faSmile} />
                                </div>
                                <div className="value-content">
                                    <h3>Joy in Learning</h3>
                                    <p>We create an environment where education is engaging, enjoyable, and inspires a lifelong love for learning.</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                {/* Approach Section */}
                <section className="approach-section">
                    <div className="container">
                        <div className="section-header reveal">
                            <span className="section-subtitle">Our Approach</span>
                            <h2 className="section-title">Educational Philosophy</h2>
                            <div className="section-description">
                                <p>At Abhyaas, we believe in a holistic approach to early childhood education</p>
                            </div>
                        </div>

                        <div className="approach-cards reveal">
                            <div className="approach-card">
                                <div className="approach-icon">
                                    <FontAwesomeIcon icon={faStar} />
                                </div>
                                <div className="approach-content">
                                    <h3>Child-Centered Learning</h3>
                                    <p>Our curriculum is designed around the needs and interests of each child, recognizing that every child learns differently and at their own pace.</p>
                                </div>
                            </div>

                            <div className="approach-card">
                                <div className="approach-icon">
                                    <FontAwesomeIcon icon={faStar} />
                                </div>
                                <div className="approach-content">
                                    <h3>Experiential Education</h3>
                                    <p>We emphasize hands-on, experiential learning that engages all senses and encourages children to explore, discover, and create.</p>
                                </div>
                            </div>

                            <div className="approach-card">
                                <div className="approach-icon">
                                    <FontAwesomeIcon icon={faStar} />
                                </div>
                                <div className="approach-content">
                                    <h3>Balanced Development</h3>
                                    <p>Our programs nurture cognitive, physical, social, emotional, and creative development to help children grow into well-rounded individuals.</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>
            </main>

            <Footer />
            <ScrollToTop />
        </div>
    );
};

export default AboutPage;